import { format } from 'date-fns';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import {
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
} from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { ISanityRewardsMap, LoyaltyReward, TLoyaltyCooldown } from 'state/loyalty/types';

import { EnrichedReward, NonNullableReward, NonNullableRewardCategory } from './types';

const hasCooldownError = (reward: EnrichedReward) =>
  reward.rewardEvaluationResults?.[0]?.code ===
  IncentiveEvaluationErrorCodes.WITHIN_COOL_DOWN_PERIOD;

/**
 * Comparator to sort rewards:
 *   - Rewards with no errors are put first
 *   - For rewards with errors, the ones with cooldown error are put at first
 */
const sortRewards = (a: EnrichedReward, b: EnrichedReward) => {
  if (!a.inlineAlertMessage && b.inlineAlertMessage) {
    return -1;
  }

  if (a.inlineAlertMessage && !b.inlineAlertMessage) {
    return 1;
  }

  if (a.inlineAlertMessage && b.inlineAlertMessage) {
    if (hasCooldownError(a) && !hasCooldownError(b)) {
      return -1;
    }

    if (!hasCooldownError(a) && hasCooldownError(b)) {
      return 1;
    }

    if (hasCooldownError(a) && hasCooldownError(b)) {
      return 0;
    }

    return a.inlineAlertMessage.localeCompare(b.inlineAlertMessage);
  }
  return 0;
};

const getRewardInlineMessage = ({
  rewardEvaluationResults,
  rewardsCooldown,
  cartEntries,
}: {
  rewardEvaluationResults: IIncentiveEvaluationResult[];
  rewardsCooldown: TLoyaltyCooldown;
  cartEntries: ILegacyCartEntry[];
}) => {
  // Reward cooldown error message takes precendence above any other error
  if (rewardsCooldown.isActive) {
    return t('rewardAvaiableAt', {
      time: format(rewardsCooldown.endsAt, 'h:mm a'),
      defaultValue: 'Redeem at {time}',
    });
  }

  const evaluationMessages = rewardEvaluationResults?.map(evaluationResult => {
    const { code, targetValue } = evaluationResult;
    if (code === IncentiveEvaluationErrorCodes.OUT_OF_DAY_PART) {
      const incentive = targetValue.incentive?.incentives?.[0];
      const isCombo = incentive._type === 'combo';

      const dayPart = isCombo
        ? incentive?.mainItem?.operationalItem?.daypart?.[0]
        : incentive?.operationalItem?.daypart?.[0];

      return `${dayPart} ${t('only')}`;
    } else if (code === IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE) {
      return t('rewardUnavailable');
    } else if (
      // Ensure cooldown no error message period if cooldown is not active
      code === IncentiveEvaluationErrorCodes.WITHIN_COOL_DOWN_PERIOD &&
      !rewardsCooldown.isActive
    ) {
      return '';
    }
    // Fallback for other messaging
    const message = getEvaluationResultMessage({
      evaluationResult,
      incentiveType: LoyaltyPromotionType.OFFER,
      cartEntries,
    });
    return message as string;
  });

  return evaluationMessages ? evaluationMessages[0] : undefined;
};

/**
 * Enrich reward with extra information, including inline alert message based on availability
 */
const enrichReward = ({
  reward,
  sanityRewardsMap,
  rewardsEvaluationMap,
  cartEntries,
  appliedRewards,
  rewardsCooldown,
}: {
  reward: NonNullableReward;
  sanityRewardsMap: ISanityRewardsMap | null;
  rewardsEvaluationMap: IncentiveEvaluationMap;
  cartEntries: ILegacyCartEntry[];
  appliedRewards?: ReadonlyArray<{ rewardId: string }>;
  rewardsCooldown: TLoyaltyCooldown;
}): EnrichedReward => {
  const rewardId = reward?._id;
  const incentive: LoyaltyReward | undefined = sanityRewardsMap?.[rewardId || ''];
  const loyaltyRewardId = incentive?.loyaltyEngineId || '';
  const rewardEvaluationResults = rewardsEvaluationMap[loyaltyRewardId];

  const inlineAlertMessage = getRewardInlineMessage({
    rewardEvaluationResults,
    rewardsCooldown,
    cartEntries,
  });

  const isApplied = Boolean(
    rewardId && appliedRewards?.some(applied => applied.rewardId === rewardId)
  );

  return {
    reward,
    rewardId,
    incentive,
    loyaltyRewardId,
    rewardEvaluationResults,
    inlineAlertMessage,
    isApplied,
  };
};

/**
 * Filter function that returns true if the reward does not have NOT_AVAILABLE_IN_STORE
 * error. It returns false otherwise
 */
const filterUnavailableRewards = ({
  reward,
  sanityRewardsMap,
  rewardsEvaluationMap,
}: {
  reward: NonNullableReward;
  sanityRewardsMap: ISanityRewardsMap | null;
  rewardsEvaluationMap: IncentiveEvaluationMap;
}) => {
  const incentive = sanityRewardsMap?.[reward?._id || ''];
  const loyaltyRewardId = incentive?.loyaltyEngineId || '';
  const rewardEvaluationResults = rewardsEvaluationMap[loyaltyRewardId];

  // No evaluations means there were no errors
  if (!rewardEvaluationResults) {
    return true;
  }

  return rewardEvaluationResults.every(
    evaluationResult =>
      evaluationResult.code !== IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE
  );
};

export const buildRewardsFromCategory = (
  category: NonNullableRewardCategory,
  {
    sanityRewardsMap,
    rewardsEvaluationMap,
    cartEntries,
    appliedRewards,
    rewardsCooldown,
  }: {
    sanityRewardsMap: ISanityRewardsMap | null;
    rewardsEvaluationMap: IncentiveEvaluationMap;
    cartEntries: ILegacyCartEntry[];
    appliedRewards?: ReadonlyArray<{ rewardId: string }>;
    rewardsCooldown: TLoyaltyCooldown;
  }
) => {
  const categoryLabel = category.label?.en;

  if (!category.rewards?.length || !categoryLabel) {
    return null;
  }

  const rewards = category.rewards
    // Discard unavailable rewards
    .filter(reward => {
      if (!reward) {
        return false;
      }

      if (
        !filterUnavailableRewards({
          reward,
          sanityRewardsMap,
          rewardsEvaluationMap,
        })
      ) {
        return false;
      }

      return true;
    })
    // Enrich rewards with extra information, including inline alert messages
    .map(reward => {
      return enrichReward({
        reward: reward as NonNullableReward,
        sanityRewardsMap,
        rewardsEvaluationMap,
        cartEntries,
        appliedRewards,
        rewardsCooldown,
      });
    })
    .sort(sortRewards);

  const appliedIndices = rewards
    .map((r, index) => (r.isApplied ? index : -1))
    .filter((index): index is number => index >= 0);

  if (!appliedIndices.length) {
    return rewards;
  }

  const appliedRewardsOrdered = appliedIndices.map(index => rewards[index]);
  const remaining = rewards.filter((_, index) => !appliedIndices.includes(index));

  return [...appliedRewardsOrdered, ...remaining];
};
